import { Column, ITooltipComp, ITooltipParams } from '@ag-grid-community/core';

import { getGuiBasedOnRowIndexAndColId, isOverflowing } from './ag-raw-tooltip.utils';

export interface AgRawMultiTooltipParams {
  // return custom element for custom cells
  // if no node is returned, default cell will be used
  getNode?: (props: { params: ITooltipParams; targetNode?: Element }) => HTMLElement | undefined;
}

export type AgRawMultiTooltipValues = (ConditionalTooltip | ForceTooltip | OverflowTooltip)[];

// conditionally shown
interface ConditionalTooltip {
  condition: (params: ITooltipParams) => boolean;
  type: 'conditional';
  value: string;
}

// this will be shown always
interface ForceTooltip {
  type: 'force-tooltip';
  value: string;
}

type OverflowTooltip = OverflowTooltipDefault | OverflowTooltipGetValue;

interface OverflowTooltipDefault {
  type: 'overflow';
  use: 'value' | 'valueFormatted';
}

interface OverflowTooltipGetValue {
  getTooltipValue: (params: ITooltipParams) => string;
  type: 'overflow';
  use: 'getValue';
}

export class AgRawMultiTooltip implements ITooltipComp {
  eGui!: HTMLDivElement;

  getGui() {
    return this.eGui;
  }

  getNode(params: ITooltipParams): Element | undefined {
    if (params.node && params.column && params.node.rowIndex != null) {
      return (
        getGuiBasedOnRowIndexAndColId(params.node.rowIndex, params.column.getUniqueId() || '') ||
        undefined
      );
    }
  }

  init(params: AgRawMultiTooltipParams & ITooltipParams) {
    const eGui = (this.eGui = document.createElement('div'));
    let targetNode = this.getNode(params);

    // let tooltipValue: string = params.valueFormatted || params.value || '';
    // let forceTooltip = false;

    if (params.location === 'cell' && params.getNode) {
      const node =
        params.getNode({
          params,
          targetNode,
        }) || null;

      if (node) {
        targetNode = node;
      }
    }

    let tooltipValues: AgRawMultiTooltipValues = [];

    if (Array.isArray(params.value)) {
      tooltipValues = params.value as AgRawMultiTooltipValues;
    } else {
      tooltipValues = [
        {
          type: 'overflow',
          use: 'value',
        },
      ];
    }

    let shouldShowOverflowingTooltip = true;

    if (targetNode) {
      if (!isOverflowing(targetNode)) {
        shouldShowOverflowingTooltip = false;
      }
    }

    tooltipValues = tooltipValues.filter((value) => {
      if (value.type === 'overflow') {
        return shouldShowOverflowingTooltip;
      }

      return true;
    });

    if (tooltipValues.length === 0) {
      if (params.hideTooltipCallback) {
        params.hideTooltipCallback();
      }

      return;
    }

    eGui.classList.add('ag-tooltip');
    eGui.innerHTML = generateTooltipHtml(tooltipValues, params);
  }
}

function generateTooltipHtml(
  tooltipValues: AgRawMultiTooltipValues,
  params: ITooltipParams
): string {
  let html = '';

  const isMultiple = tooltipValues.length > 1;

  for (let i = 0; i < tooltipValues.length; i++) {
    const value = tooltipValues[i];

    const addValue = (value: string) => {
      const isLast = i === tooltipValues.length - 1;

      const valueStyles = isMultiple && !isLast ? 'margin-bottom: 5px;' : '';

      html += `<li style="${valueStyles}">${value}</li>`;
    };

    switch (value.type) {
      case 'conditional': {
        if (value.condition(params)) {
          addValue(value.value || '');
        }

        break;
      }

      case 'force-tooltip': {
        addValue(value.value || '');
        break;
      }

      case 'overflow': {
        handleOverflowTooltip(value, params, addValue);
        break;
      }

      default: {
        break;
      }
    }
  }

  const containerStyles = isMultiple ? 'list-style-type: disc; padding-left: 5px;' : '';

  return `<ul style="${containerStyles}">${html}</ul>`;
}

function handleOverflowTooltip(
  value: OverflowTooltip,
  params: ITooltipParams,
  addValue: (value: string) => void
): void {
  switch (value.use) {
    case 'getValue': {
      addValue(value.getTooltipValue(params) || '');
      break;
    }

    case 'value': {
      if (params.location !== 'cell') {
        addValue(params.value || '');
        break;
      }

      const value = params.api.getValue((params.column as Column).getColId() || '', params.node!);

      addValue(value || '');
      break;
    }

    case 'valueFormatted': {
      addValue(params.valueFormatted || '');
      break;
    }

    default: {
      break;
    }
  }
}
